/** @type {Map<string, boolean>} */
const unavailableGroupCache = new Map()

/** @type {number | undefined} */
let updateTimeout

/** @type {number | undefined} */
let initInterval

/**
 * @typedef {'available' | 'sold-out' | 'hidden'} DeliveryState
 */

/**
 * @returns {HTMLElement[]}
 */
function getDeliveryInfoElements() {
  return [...document.querySelectorAll('add-to-cart-component .delivery-info')].filter(
    (element) => element instanceof HTMLElement
  )
}

/**
 * @returns {HTMLButtonElement[]}
 */
function getAddToCartButtons() {
  const selectors = [
    'add-to-cart-component [ref="addToCartButton"]',
    'add-to-cart-component button[name="add"]',
    'product-form-component button[name="add"]',
    'product-form-component button[type="submit"]',
  ]

  const buttons = new Set()

  for (const selector of selectors) {
    for (const element of document.querySelectorAll(selector)) {
      if (element instanceof HTMLButtonElement) {
        buttons.add(element)
      }
    }
  }

  return [...buttons]
}

/**
 * @returns {boolean}
 */
function isBundleSelectionComplete() {
  const input = document.querySelector('input[name="properties[_bundle_selection]"]')

  return input instanceof HTMLInputElement && input.value.trim().length > 0
}

/**
 * @returns {Element | null}
 */
function getProductSection() {
  return (
    document.getElementById('bundle-selector-root')?.closest('.shopify-section') ??
    document.querySelector('product-form-component')?.closest('.shopify-section') ??
    null
  )
}

/**
 * @param {string} configUrl
 * @param {string} productId
 * @param {string | undefined} variantId
 * @returns {Promise<boolean>}
 */
async function hasFullyUnavailableBundleGroup(configUrl, productId, variantId) {
  const cacheKey = `${productId}:${variantId ?? ''}`
  const cached = unavailableGroupCache.get(cacheKey)

  if (cached !== undefined) {
    return cached
  }

  try {
    const url = new URL(configUrl, window.location.origin)
    url.searchParams.set('product_id', productId)

    if (variantId) {
      url.searchParams.set('variant_id', variantId)
    }

    const response = await fetch(url)

    if (!response.ok) {
      return false
    }

    const config = await response.json()
    const groups = config.groups ?? []
    const hasUnavailableGroup = groups.some(
      (/** @type {{ variants?: { availableForSale?: boolean }[] }} */ group) =>
        !group.variants?.some((variant) => variant.availableForSale)
    )

    unavailableGroupCache.set(cacheKey, hasUnavailableGroup)

    return hasUnavailableGroup
  } catch {
    return false
  }
}

/**
 * @returns {boolean}
 */
function isAddToCartDisabled() {
  const buttons = getAddToCartButtons()

  if (buttons.length === 0) {
    return true
  }

  return buttons.every((button) => button.disabled)
}

/**
 * @param {HTMLElement} deliveryInfo
 * @returns {string}
 */
function getDeliveryText(deliveryInfo) {
  return deliveryInfo.dataset.deliveryText ?? ''
}

/**
 * @param {HTMLElement} deliveryInfo
 * @returns {string}
 */
function getSoldOutText(deliveryInfo) {
  return deliveryInfo.dataset.soldOutText ?? Theme.translations.sold_out ?? 'Sold out'
}

/**
 * @param {HTMLElement} deliveryInfo
 * @param {DeliveryState} state
 */
function setDeliveryInfoState(deliveryInfo, state) {
  const textElement = deliveryInfo.querySelector('.delivery-info__text')

  if (!(textElement instanceof HTMLElement)) {
    return
  }

  deliveryInfo.hidden = state === 'hidden'
  deliveryInfo.classList.toggle('delivery-info--hidden', state === 'hidden')
  deliveryInfo.classList.toggle('delivery-info--sold-out', state === 'sold-out')

  if (state === 'sold-out') {
    textElement.textContent = getSoldOutText(deliveryInfo)
    return
  }

  if (state === 'available') {
    textElement.textContent = getDeliveryText(deliveryInfo)
  }
}

/**
 * @param {boolean} hasUnavailableGroup
 * @returns {DeliveryState}
 */
function getDeliveryState(hasUnavailableGroup) {
  const selectionComplete = isBundleSelectionComplete()
  const addToCartDisabled = isAddToCartDisabled()

  if (hasUnavailableGroup || (selectionComplete && addToCartDisabled)) {
    return 'sold-out'
  }

  if (!selectionComplete || addToCartDisabled) {
    return 'hidden'
  }

  return 'available'
}

async function updateBundleDeliveryInfo() {
  const bundleRoot = document.getElementById('bundle-selector-root')
  const deliveryInfos = getDeliveryInfoElements()

  if (!bundleRoot || deliveryInfos.length === 0) {
    return
  }

  const { configUrl, productId, variantId } = bundleRoot.dataset

  let hasUnavailableGroup = false

  if (configUrl && productId) {
    hasUnavailableGroup = await hasFullyUnavailableBundleGroup(configUrl, productId, variantId)
  }

  const state = getDeliveryState(hasUnavailableGroup)

  for (const deliveryInfo of deliveryInfos) {
    setDeliveryInfoState(deliveryInfo, state)
  }
}

function scheduleUpdate() {
  if (updateTimeout) {
    window.clearTimeout(updateTimeout)
  }

  updateTimeout = window.setTimeout(() => {
    updateBundleDeliveryInfo()
  }, 50)
}

function observeBundleDeliveryInfo() {
  const productSection = getProductSection()

  scheduleUpdate()

  if (!productSection) {
    window.setTimeout(observeBundleDeliveryInfo, 250)
    return
  }

  const observer = new MutationObserver(() => {
    scheduleUpdate()
  })

  observer.observe(productSection, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['disabled', 'hidden', 'value'],
  })

  let attempts = 0
  initInterval = window.setInterval(() => {
    scheduleUpdate()
    attempts += 1

    if (attempts >= 20) {
      window.clearInterval(initInterval)
    }
  }, 250)
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeBundleDeliveryInfo)
} else {
  observeBundleDeliveryInfo()
}
